import { createSlice } from '@reduxjs/toolkit'
import { Auth } from 'aws-amplify'
import { API } from 'src/utils/interceptor'
import { editProduct } from 'src/features/ProductSlice'
import Sentry from 'src/utils/sentry'
import { getPriceChangeActor } from 'src/utils/getPriceChangeActor'

const initialState = {
  term: '',
  products: [],
  fields: ['title', 'sku'],
  selectedSupplierTitle: '',
  selectedProductType: '',
  productTypeOptions: [],
  page: 1,
  pageSize: 50,
  total: 0,
  loading: false,
  /** True after at least one search finishes — avoids a premature empty state. */
  hasCompletedSearch: false,
  error: '',
}

const catalogueSearchSlice = createSlice({
  name: 'catalogueSearch',
  initialState,

  reducers: {
    setTerm: (state, action) => {
      state.term = action.payload
    },
    setProducts: (state, action) => {
      state.products = action.payload
    },
    setFields: (state, action) => {
      state.fields = action.payload
    },
    setSelectedSupplierTitle: (state, action) => {
      state.selectedSupplierTitle = String(action.payload || '').trim()
    },
    setSelectedProductType: (state, action) => {
      state.selectedProductType = String(action.payload || '').trim()
    },
    setProductTypeOptions: (state, action) => {
      const incoming = Array.isArray(action.payload) ? action.payload : []
      const merged = new Set(
        [...state.productTypeOptions, ...incoming]
          .map(type => String(type || '').trim())
          .filter(Boolean),
      )
      state.productTypeOptions = [...merged].sort((a, b) => a.localeCompare(b))
    },
    setPage: (state, action) => {
      state.page = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    },
    setTotal: (state, action) => {
      state.total = action.payload
    },
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setHasCompletedSearch: (state, action) => {
      state.hasCompletedSearch = Boolean(action.payload)
    },
    setError: (state, action) => {
      state.error = action.payload
    },
    editSearchProduct: (state, action) => {
      const index = state.products.findIndex(
        product => product._id === action.payload._id,
      )
      if (index !== -1) state.products[index] = action.payload
    },
    resetCatalogueSearch: state => {
      state = initialState
      return state
    },
  },
})

export const {
  setTerm,
  setProducts,
  setFields,
  setSelectedSupplierTitle,
  setSelectedProductType,
  setProductTypeOptions,
  setPage,
  setPageSize,
  setTotal,
  setLoading,
  setHasCompletedSearch,
  setError,
  editSearchProduct,
  setCatalougeSearchFromSuppliers,
  clearCatalougeFilter,
  resetCatalogueSearch,
} = catalogueSearchSlice.actions

export function resolveSupplierFilterValues(
  venueSuppliers = [],
  selectedSupplierTitle = '',
) {
  const selectedTitle = String(selectedSupplierTitle || '')
    .trim()
    .toLowerCase()

  if (!selectedTitle) {
    return []
  }

  return venueSuppliers
    .filter(
      venueSupplier =>
        venueSupplier?.isActive === true &&
        venueSupplier?.isCatalogueAccessible === true,
    )
    .filter(venueSupplier => {
      const supplierTitle = String(
        venueSupplier?.supplierTitle || venueSupplier?.supplier?.title || '',
      )
        .trim()
        .toLowerCase()
      return supplierTitle === selectedTitle
    })
    .map(venueSupplier => {
      const supplierId = venueSupplier?.supplier?._id || venueSupplier?.supplier
      return supplierId ? String(supplierId) : null
    })
    .filter(Boolean)
}

export const performSearch =
  ({ venueId, pageNumber, pageSize }) =>
  async (dispatch, getState) => {
    const rawTerm = getState().catalogueSearch.term.trimEnd()
    // Typesense needs a query string; '*' matches all within filter_by scope
    const searchTerm = rawTerm || '*'
    const searchFields = getState().catalogueSearch.fields
    const selectedSupplierTitle =
      getState().catalogueSearch.selectedSupplierTitle
    const selectedProductType = getState().catalogueSearch.selectedProductType
    const venueSuppliers = getState().venueSuppliers?.venueSuppliers || []
    const filterValues = resolveSupplierFilterValues(
      venueSuppliers,
      selectedSupplierTitle,
    )

    dispatch(setLoading(true))

    const searchPath = venueId ? `venue/${venueId}/catalogue` : `products`

    try {
      const session = await Auth.currentAuthenticatedUser()
      const searchResults = await API.post('api', searchPath, {
        body: {
          searchTerm,
          searchFields,
          searchPage: pageNumber,
          searchPerPage: pageSize,
          filterValues,
          productType: selectedProductType || undefined,
        },
        queryStringParameters: {
          username: session.attributes.sub,
        },
      })
      dispatch(setTotal(searchResults.total))
      dispatch(setProducts(searchResults.results))
      dispatch(setPageSize(searchResults.pageSize))
      dispatch(setPage(searchResults.pageNumber))
      if (Array.isArray(searchResults.productTypes)) {
        dispatch(setProductTypeOptions(searchResults.productTypes))
      }
      dispatch(setHasCompletedSearch(true))
      dispatch(setLoading(false))
    } catch (e) {
      console.error('Got error response from search endpoint:', e)
      Sentry.captureException(e)
      dispatch(setHasCompletedSearch(true))
      dispatch(setLoading(false))
      dispatch(
        setError('Something went wrong when searching. Please try again.'),
      )
    }
  }

export const adminEditProductFromSearch = product => async dispatch => {
  try {
    const changedBy = await getPriceChangeActor()
    const _product = await API.post('api', `product`, {
      body: { ...product, changedBy },
    })
    dispatch(editSearchProduct(_product))
    dispatch(editProduct(_product))
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}

export default catalogueSearchSlice
